import { useMemo, useState, type FormEvent } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { sharedCostKeywordsForEditor } from '@/utils/sharedCostKeywords'

type Props = {
  keywordsRaw: string
  saving: boolean
  onSave: (value: string) => Promise<void>
}

export default function SharedCostKeywordsEditor({ keywordsRaw, saving, onSave }: Props) {
  const [draft, setDraft] = useState('')

  const keywords = useMemo(() => sharedCostKeywordsForEditor(keywordsRaw), [keywordsRaw])

  async function saveKeywords(next: string[]) {
    const normalized = [...new Set(next.map((phrase) => phrase.trim().toLowerCase()).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
    await onSave(JSON.stringify(normalized))
  }

  async function addKeyword(raw: string) {
    const phrase = raw.trim().toLowerCase()
    if (!phrase) return
    if (keywords.some((existing) => existing === phrase)) {
      setDraft('')
      return
    }
    await saveKeywords([...keywords, phrase])
    setDraft('')
  }

  async function removeKeyword(phrase: string) {
    await saveKeywords(keywords.filter((existing) => existing !== phrase))
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    void addKeyword(draft)
  }

  return (
    <div className="w-full space-y-4">
      <p className="text-sm leading-relaxed text-gray-600">
        Phrases that mark line descriptions as shared costs. Matching is case-insensitive.
      </p>
      <p className="text-xs text-gray-500">{keywords.length} keywords</p>
      <form onSubmit={handleSubmit} className="flex w-full max-w-[800px] gap-2">
        <label className="sr-only" htmlFor="settings-shared-cost-keyword">
          Add shared cost keyword
        </label>
        <input
          id="settings-shared-cost-keyword"
          type="text"
          className="input flex-1"
          placeholder="Add phrase"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          disabled={saving}
        />
        <button
          type="submit"
          className="btn btn-secondary shrink-0"
          disabled={saving || !draft.trim()}
        >
          Add
        </button>
      </form>
      {keywords.length === 0 ? (
        <p className="text-sm text-gray-500">None — no lines will match by description.</p>
      ) : (
        <ul className="flex flex-wrap gap-2">
          {keywords.map((phrase) => (
            <li key={phrase}>
              <span className="inline-flex max-w-full items-center gap-1 rounded-full bg-white py-1 pl-2.5 pr-1 text-sm text-gray-800 ring-1 ring-gray-200/80">
                <span className="max-w-[12rem] truncate sm:max-w-[20rem]">{phrase}</span>
                <button
                  type="button"
                  className="rounded-full p-0.5 text-gray-500 transition-colors hover:bg-black/5 hover:text-gray-800"
                  aria-label={`Remove ${phrase}`}
                  onClick={() => void removeKeyword(phrase)}
                  disabled={saving}
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
